import * as yup from 'yup';
import { validate as isUUID } from 'uuid';
import { SinglePhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';

const cropInfoSchema = yup
  .object({
    unit: yup.string().oneOf(['percent']).required(),
    x: yup.number().required(),
    y: yup.number().required(),
    width: yup.number().required(),
    height: yup.number().required(),
  })
  .noUnknown();

const photoSchema = yup
  .object({
    id: yup
      .string()
      .required()
      .test('is-uuid', 'Invalid UUID format', value => value && isUUID(value)),
    height: yup.number().required(),
    width: yup.number().required(),
    url: yup.string().url().required(),
    cropInfo: cropInfoSchema.required(),
    mobileCropInfo: cropInfoSchema.notRequired(),
  })
  .noUnknown();

const headerColorsSchema = yup
  .object({
    nameAndNavFontColor: yup.string().notRequired(),
    nameAndNavBackgroundColor: yup.string().notRequired(),
    nameAndNavBackgroundColorEnabled: yup.boolean().notRequired(),
  })
  .noUnknown();

const eventNameSchema = yup
  .object({
    content: yup.string(),
    textAlignment: yup.string().oneOf(['left', 'center', 'right']).required(),
    pageAlignment: yup
      .object({
        x: yup.string().oneOf(['left', 'center', 'right']).required(),
        y: yup.string().oneOf(['top', 'center', 'bottom']).required(),
      })
      .noUnknown()
      .notRequired(),
    hasModifiedEventNameOnce: yup.boolean().notRequired(),
  })
  .noUnknown();

const headerLayoutDataJSONSchema = yup
  .object({
    $id: yup.string().url().required(),
    presentationLayout: yup.string().required(),
    colors: headerColorsSchema.notRequired(),
    photos: yup.array(photoSchema).notRequired(),
    eventName: eventNameSchema.notRequired(),
    desktopVideoUrl: yup.string().notRequired(),
    mobileVideoUrl: yup.string().notRequired(),
    thumbnailImageUrl: yup.string().notRequired(),
    jsonContainerId: yup.string().nullable().notRequired(),
  })
  .noUnknown();

const websiteDesignSettingsSchema = yup
  .object({
    colors: yup
      .object({
        artBackgroundColor: yup.string().required(),
        artFontColor: yup.string().required(),
        pageBackgroundColor: yup.string().required(),
        pageFontColor: yup.string().required(),
        linkAndButtonColor: yup.string().required(),
      })
      .noUnknown(),
    fontPackName: yup.string().required(),
    pageTransparency: yup.boolean().required(),
    pageShadow: yup.boolean().required(),
    welcomePhotoPresentationLayout: yup.string().notRequired(),
  })
  .noUnknown();

const headerLayoutSchema = yup
  .object({
    dataJSON: headerLayoutDataJSONSchema.required(),
    schemaURL: yup.string().url().required(),
    websiteLayout: yup.string().required(),
  })
  .noUnknown();

export const websiteDesignSchema = yup
  .object({
    template: yup.string().required(),
    theme: yup.string().required(),
    websiteDesignSettings: websiteDesignSettingsSchema.required(),
    headerLayout: headerLayoutSchema.required(),
  })
  .noUnknown();

// Need to make changes in marcom templates to make the property names consistent with the website design settings schema
export const websiteDesignSettingsQueryParamSchema = yup.object({
  colors: yup
    .object({
      linkAndButton: yup.string().required(),
      artBackground: yup.string().required(),
      artFont: yup.string().required(),
      pageBackground: yup.string().required(),
      pageFont: yup.string().required(),
    })
    .required()
    .noUnknown(),
  pageTransparency: yup.boolean().required(),
  pageShadow: yup.boolean().required(),
  photoPresentationLayout: yup
    .string()
    .oneOf(['center', 'fullWidth', 'pageWidth'] as SinglePhotoPresentationLayout[])
    .notRequired(),
  fontPackName: yup.string().notRequired(),
  isBlankTemplate: yup.boolean().notRequired(),
});

export const isWebsiteDesignSettingsFromQueryParamValid = (decodedSettings: Record<string, unknown>): boolean => {
  return websiteDesignSettingsQueryParamSchema.isValidSync(decodedSettings);
};
